import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Phone, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  {
    icon: Mail,
    label: "Email",
    value: "admin@dmabsolutions.com.au",
    href: "mailto:admin@dmabsolutions.com.au",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "0494 523 118",
    href: "tel:0494523118",
  },
  {
    icon: MapPin,
    label: "Office",
    value: "21 Crombie Avenue, Bundall QLD 4217",
    href: "https://maps.google.com/?q=21+Crombie+Avenue+Bundall+QLD+4217",
  },
];

const FIELD =
  "w-full bg-white/[0.06] border border-white/15 rounded-xl px-4 py-3.5 text-white placeholder:text-white/40 outline-none transition-all duration-300 focus:border-[#00F0FF] focus:bg-white/[0.09] focus:ring-1 focus:ring-[#00F0FF]/40";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in every field before sending.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! Your message is on its way to our team.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Could not send your message. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 lg:py-40 border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          {/* Left: info */}
          <Reveal>
            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-12 bg-[#00F0FF]" />
              <span className="text-sm uppercase tracking-[0.25em] text-[#00F0FF]">
                Contact
              </span>
            </div>
            <h2 className="font-display font-bold tracking-tighter text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              Let's build something dependable.
            </h2>
            <p className="mt-6 text-lg font-light text-[#94A3B8] max-w-md leading-relaxed">
              Tell us about your project or challenge. We reply to every enquiry
              personally, usually within one business day.
            </p>

            <div className="mt-12 space-y-4">
              {INFO.map((item) => {
                const Icon = item.icon;
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    target={item.label === "Office" ? "_blank" : undefined}
                    rel="noreferrer"
                    data-testid={`contact-info-${item.label.toLowerCase()}`}
                    className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#0C1322] p-5 transition-colors duration-300 hover:border-[#00F0FF]/60"
                  >
                    <span className="grid place-items-center h-12 w-12 rounded-xl bg-white/5 text-[#00F0FF] transition-colors duration-300 group-hover:bg-[#00F0FF] group-hover:text-[#050A15]">
                      <Icon size={20} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                        {item.label}
                      </div>
                      <div className="mt-1 text-white font-medium truncate">
                        {item.value}
                      </div>
                    </div>
                    <ArrowUpRight
                      size={18}
                      className="ml-auto text-white/30 transition-colors group-hover:text-[#00F0FF]"
                    />
                  </a>
                );
              })}
            </div>
          </Reveal>

          {/* Right: form */}
          <Reveal delay={0.15}>
            <motion.form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="rounded-3xl border border-white/15 bg-[#0C1322] p-7 md:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
            >
              <div className="space-y-5">
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">
                    Name
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your full name"
                    data-testid="contact-input-name"
                    className={FIELD}
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">
                    Email
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    data-testid="contact-input-email"
                    className={FIELD}
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">
                    Contact number
                  </label>
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="e.g. 0494 523 118"
                    data-testid="contact-input-phone"
                    className={FIELD}
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#94A3B8] mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={5}
                    placeholder="Tell us about your project…"
                    data-testid="contact-input-message"
                    className={`${FIELD} resize-none`}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#00F0FF] px-8 py-4 font-display font-semibold text-[#050A15] transition-all duration-300 hover:bg-[#33F3FF] hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {loading ? (
                    <>
                      Sending
                      <Loader2 size={18} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Send enquiry
                      <ArrowUpRight
                        size={18}
                        className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </>
                  )}
                </button>
              </div>
            </motion.form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
